import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getChildMaintenances,
  getMaintenance,
  getMaintenancesUpcomingMaterialized,
  getPaginatedMaintenancesHistory,
  getPaginatedMaintenancesOpen,
} from "@/api/resources/maintenances/api";
import { transformPaginatedMaintenances } from "@/api/resources/maintenances/selectors";
import i18n from "@/providers/i18n";
import { type QueryFilter } from "@/utils/filter";
import { stringifyQueryPaginationFilters, type QueryPaginationFilters } from "@/utils/pagination";

const entity = i18n.t("maintenance");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("maintenances");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const maintenancesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "maintenances"],
  lists: () => [...maintenancesQueryKeys.all(), "list"],
  listChildMaintenances: (maintenance_id?: string) => [
    ...maintenancesQueryKeys.lists(),
    "childMaintenances",
    maintenance_id,
  ],
  listUpcomingMaterialized: (customFilters?: QueryFilter[]) => [
    ...maintenancesQueryKeys.lists(),
    "upcomingMaterialized",
    customFilters,
  ],
  listOpen: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...maintenancesQueryKeys.lists(),
    "open",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  listHistory: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...maintenancesQueryKeys.lists(),
    "history",
    stringifyQueryPaginationFilters(queryKeyPaginationFilters),
  ],
  byId: (id?: string) => [...maintenancesQueryKeys.all(), "byId", id],
};

export const maintenancesQueries = {
  listChildMaintenances: (maintenance_id?: string) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listChildMaintenances(maintenance_id)],
      queryFn: maintenance_id ? () => getChildMaintenances(maintenance_id) : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listUpcomingMaterialized: (customFilters?: QueryFilter[]) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listUpcomingMaterialized(customFilters)],
      queryFn: () => getMaintenancesUpcomingMaterialized(customFilters),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listOpen: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listOpen(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedMaintenancesOpen(queryKeyPaginationFilters),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({
        count,
        data: transformPaginatedMaintenances(data),
      }),
    }),
  listHistory: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listHistory(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedMaintenancesHistory(queryKeyPaginationFilters),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({
        count,
        data: transformPaginatedMaintenances(data),
      }),
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.byId(id)],
      queryFn: id ? () => getMaintenance({ match: { id } }) : skipToken,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
